import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';

// ---------------------------------------------------------------------------
// Types matching the Rust ServerMessage / ClientMessage enums
// ---------------------------------------------------------------------------

/** Server → Client messages (tagged union on `type`) */
export type ServerMessage =
  | {
      type: 'cqrs_events';
      workspace_id: string;
      events: unknown[]; // StoredEvents — opaque to JS, passed to WASM as JSON
      correlation_id: string | null;
    }
  | {
      type: 'conversation_message';
      message: unknown; // ConversationMessage
    }
  | {
      type: 'conversation_status_change';
      conversation_id: string;
      status: { type: string };
    }
  | {
      type: 'command_error';
      correlation_id: string;
      error: string;
    }
  | {
      type: 'authenticated';
      user_id: string;
    }
  | {
      type: 'auth_error';
      error: string;
    }
  | {
      type: 'subscribed';
      entity_id: string;
    }
  | {
      type: 'command_accepted';
      correlation_id: string;
    };

/** Client → Server messages (tagged union on `action`) */
type ClientMessage =
  | {
      action: 'authenticate';
      token: string;
    }
  | {
      action: 'subscribe';
      entity_id: string;
      after_event_id?: number;
    }
  | {
      action: 'unsubscribe';
      entity_id: string;
    }
  | {
      action: 'command';
      workspace_id: string;
      commands: unknown[];
      correlation_id: string;
    };

// ---------------------------------------------------------------------------
// Command batch queue types
// ---------------------------------------------------------------------------

export type CommandBatchStatus = 'queued' | 'in_flight' | 'confirmed' | 'failed';

export interface CommandBatch {
  correlationId: string;
  commands: unknown[];
  status: CommandBatchStatus;
  error?: string;
}

// ---------------------------------------------------------------------------
// Callbacks for consumers to register
// ---------------------------------------------------------------------------

export interface WebSocketCallbacks {
  /** Called when CQRS events arrive (backfill or live). Consumer passes them to WASM. */
  onCqrsEvents?: (
    workspaceId: string,
    events: unknown[],
    correlationId: string | null,
  ) => void;
  /** Called when a conversation message arrives from inference. */
  onConversationMessage?: (message: unknown) => void;
  /** Called when conversation status changes (idle, inferring, awaiting_approval). */
  onConversationStatusChange?: (conversationId: string, status: { type: string }) => void;
  /** Called when a command batch fails validation on the server. */
  onCommandError?: (correlationId: string, error: string) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const WS_URL = process.env.WS_URL ?? '';

export function useWebSocket(callbacks: WebSocketCallbacks) {
  const { token } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [subscribedEntities, setSubscribedEntities] = useState<Set<string>>(
    new Set(),
  );

  // Command batch queue: at most one in-flight at a time
  const batchQueueRef = useRef<CommandBatch[]>([]);
  const [inFlightBatch, setInFlightBatch] = useState<CommandBatch | null>(null);

  // Keep callbacks ref-stable to avoid reconnects
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // Pending subscriptions to send once connected
  const pendingSubscriptionsRef = useRef<
    { entityId: string; afterEventId?: number }[]
  >([]);

  // ── Send a raw message over the WebSocket ──────────────────────
  const sendMessage = useCallback((msg: ClientMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }, []);

  // ── Flush the next queued batch if nothing is in-flight ────────
  const flushNextBatch = useCallback(() => {
    const queue = batchQueueRef.current;
    if (queue.length === 0) return;

    // Find the first queued batch
    const idx = queue.findIndex((b) => b.status === 'queued');
    if (idx === -1) return;

    const batch = queue[idx];
    batch.status = 'in_flight';
    setInFlightBatch({ ...batch });

    // The command message requires a workspace_id. We embed it in the batch
    // at enqueue time (see submitCommands).
    const msg = (batch as any)._message as ClientMessage;
    sendMessage(msg);
  }, [sendMessage]);

  // ── Handle incoming server messages ────────────────────────────
  const handleMessage = useCallback(
    (data: string) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(data);
      } catch {
        console.warn('[ws] invalid JSON from server:', data);
        return;
      }

      switch (msg.type) {
        case 'cqrs_events': {
          callbacksRef.current.onCqrsEvents?.(
            msg.workspace_id,
            msg.events,
            msg.correlation_id,
          );

          // If this confirms our in-flight batch, mark it and flush next
          if (msg.correlation_id) {
            const queue = batchQueueRef.current;
            const idx = queue.findIndex(
              (b) => b.correlationId === msg.correlation_id,
            );
            if (idx !== -1) {
              queue[idx].status = 'confirmed';
              setInFlightBatch(null);
              // Remove confirmed batches from the front
              while (queue.length > 0 && queue[0].status === 'confirmed') {
                queue.shift();
              }
              flushNextBatch();
            }
          }
          break;
        }

        case 'conversation_message':
          callbacksRef.current.onConversationMessage?.(msg.message);
          break;

        case 'conversation_status_change':
          callbacksRef.current.onConversationStatusChange?.(
            msg.conversation_id,
            msg.status,
          );
          break;

        case 'command_error': {
          callbacksRef.current.onCommandError?.(
            msg.correlation_id,
            msg.error,
          );

          // Mark the failed batch and stop — user must resolve before next batch
          const queue = batchQueueRef.current;
          const idx = queue.findIndex(
            (b) => b.correlationId === msg.correlation_id,
          );
          if (idx !== -1) {
            queue[idx].status = 'failed';
            queue[idx].error = msg.error;
            setInFlightBatch(null);
            // Do NOT flush next — block until user resolves the failed batch
          }
          break;
        }

        case 'authenticated': {
          console.log('[ws] authenticated as', msg.user_id);
          setConnected(true);

          // Re-subscribe to all previously active entities (handles reconnect)
          for (const sub of activeSubscriptionsRef.current.values()) {
            const subMsg: ClientMessage = {
              action: 'subscribe',
              entity_id: sub.entityId,
              ...(sub.afterEventId != null
                ? { after_event_id: sub.afterEventId }
                : {}),
            };
            wsRef.current?.send(JSON.stringify(subMsg));
          }

          // Also flush any pending subscriptions added before connection
          for (const sub of pendingSubscriptionsRef.current) {
            const subMsg: ClientMessage = {
              action: 'subscribe',
              entity_id: sub.entityId,
              ...(sub.afterEventId != null
                ? { after_event_id: sub.afterEventId }
                : {}),
            };
            wsRef.current?.send(JSON.stringify(subMsg));
          }
          pendingSubscriptionsRef.current = [];
          break;
        }

        case 'auth_error':
          console.error('[ws] auth failed:', msg.error);
          wsRef.current?.close();
          break;

        case 'subscribed': {
          setSubscribedEntities((prev) => new Set(prev).add(msg.entity_id));
          break;
        }

        case 'command_accepted':
          // Ack received — command is in the SQS queue. No action needed;
          // we wait for the cqrs_events or command_error with this correlation_id.
          break;

        default:
          console.warn('[ws] unknown server message type:', (msg as any).type);
      }
    },
    [flushNextBatch],
  );

  // ── Connect / reconnect ────────────────────────────────────────
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const intentionalCloseRef = useRef(false);
  // Track subscribed entities for re-subscription on reconnect
  const activeSubscriptionsRef = useRef<
    Map<string, { entityId: string; afterEventId?: number }>
  >(new Map());

  const connect = useCallback(() => {
    if (!token || !WS_URL) return;

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[ws] connected, authenticating...');
      reconnectAttemptRef.current = 0;
      ws.send(JSON.stringify({ action: 'authenticate', token }));
    };

    ws.onmessage = (event) => {
      handleMessage(event.data);
    };

    ws.onclose = (event) => {
      wsRef.current = null;

      // Auto-reconnect unless we closed intentionally (unmount)
      if (!intentionalCloseRef.current) {
        console.log('[ws] connection lost, reconnecting...', event.code, event.reason);
        const attempt = reconnectAttemptRef.current;
        const delay = Math.min(1000 * 2 ** attempt, 30000); // 1s, 2s, 4s, ... 30s max
        console.log(`[ws] reconnecting in ${delay}ms (attempt ${attempt + 1})`);
        reconnectTimerRef.current = setTimeout(() => {
          reconnectAttemptRef.current = attempt + 1;
          connect();
        }, delay);
      } else {
        console.log('[ws] disconnected (intentional)');
        setConnected(false);
      }
    };

    ws.onerror = () => {
      // onclose will fire after this — reconnect logic is there
    };
  }, [token, handleMessage]);

  // Use a ref so the effect doesn't re-run when connect's identity changes
  const connectRef = useRef(connect);
  connectRef.current = connect;

  useEffect(() => {
    intentionalCloseRef.current = false;
    connectRef.current();

    return () => {
      intentionalCloseRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      wsRef.current?.close();
      wsRef.current = null;
      setConnected(false);
    };
  }, [token]); // Only reconnect when token changes

  // ── Public API ─────────────────────────────────────────────────

  /** Subscribe to events for a workspace or conversation. */
  const subscribe = useCallback(
    (entityId: string, afterEventId?: number) => {
      // Track for re-subscription on reconnect
      activeSubscriptionsRef.current.set(entityId, { entityId, afterEventId });

      const msg: ClientMessage = {
        action: 'subscribe',
        entity_id: entityId,
        ...(afterEventId != null ? { after_event_id: afterEventId } : {}),
      };

      if (wsRef.current?.readyState === WebSocket.OPEN && connected) {
        sendMessage(msg);
      } else {
        pendingSubscriptionsRef.current.push({ entityId, afterEventId });
      }
    },
    [sendMessage, connected],
  );

  /** Unsubscribe from an entity. */
  const unsubscribe = useCallback(
    (entityId: string) => {
      activeSubscriptionsRef.current.delete(entityId);
      sendMessage({ action: 'unsubscribe', entity_id: entityId });
      setSubscribedEntities((prev) => {
        const next = new Set(prev);
        next.delete(entityId);
        return next;
      });
    },
    [sendMessage],
  );

  /**
   * Submit CQRS commands via WebSocket.
   *
   * Commands are queued and sent one batch at a time. The next batch is
   * only sent after the current one is confirmed (CqrsEvents with matching
   * correlation_id) or the user resolves a failed batch.
   *
   * Returns the correlation_id for the queued batch.
   */
  const submitCommands = useCallback(
    (workspaceId: string, commands: unknown[]): string => {
      const correlationId = crypto.randomUUID();
      const wsMessage: ClientMessage = {
        action: 'command',
        workspace_id: workspaceId,
        commands,
        correlation_id: correlationId,
      };

      const batch: CommandBatch & { _message: ClientMessage } = {
        correlationId,
        commands,
        status: 'queued',
        _message: wsMessage,
      };

      batchQueueRef.current.push(batch);

      // If nothing is in-flight, send immediately
      const hasInFlight = batchQueueRef.current.some(
        (b) => b.status === 'in_flight',
      );
      if (!hasInFlight) {
        flushNextBatch();
      }

      return correlationId;
    },
    [flushNextBatch],
  );

  /**
   * Dismiss a failed batch so the next queued batch can proceed.
   * The consumer should revert or discard the failed commands first.
   */
  const dismissFailedBatch = useCallback(
    (correlationId: string) => {
      const queue = batchQueueRef.current;
      const idx = queue.findIndex((b) => b.correlationId === correlationId);
      if (idx !== -1) {
        queue.splice(idx, 1);
        flushNextBatch();
      }
    },
    [flushNextBatch],
  );

  return {
    connected,
    subscribedEntities,
    inFlightBatch,
    subscribe,
    unsubscribe,
    submitCommands,
    dismissFailedBatch,
  };
}
